import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useBoolean } from '@fluentui/react-hooks';
import {
  DefaultButton,
  FontIcon,
  IconButton,
  MessageBarType,
  PrimaryButton,
  SearchBox,
  Spinner
} from '@fluentui/react';
import { useTranslation } from 'react-i18next';

// models
import { Staff } from '../../../models/Entities/Staff/Staff';
import { PersonTypeEnum } from '../../../models/Entities/Person/PersonCommands';

// components
import { ConfirmationButtonComponent } from '../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import { PaginationControlComponent } from '../../../commons/component/PaginationControl/PaginationControl.Component';
import PersonFormComponent from '../PersonForm/Person.Form.Component';

// functions
import * as ComponentFunctions from './Staff.List.Functions';

// styles
import './Staff.List.Styles.css';

const StaffListComponent = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();
  const navigate = useNavigate();

  const [isFormOpen, { setTrue: openForm, setFalse: dismissForm }] = useBoolean(false);
  const [selectedStaffId, setSelectedStaffId] = useState<number | undefined>(undefined);
  const [selectedPersonId, setSelectedPersonId] = useState<number | undefined>(undefined);

  const [searchText, setSearchText] = useState<string>('');
  const [appliedSearchText, setAppliedSearchText] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(ComponentFunctions.DefaultPageSize);

  /**
   * Tarjeta que queda abierta aunque el mouse se vaya.
   *
   * Existe por la confirmación de borrado: se dibuja en un callout fuera de la
   * tarjeta, así que al mover el puntero hacia él la tarjeta perdería el hover,
   * se encogería y se llevaría el botón que lo abrió de abajo del cursor. Se
   * ancla al tocar la zona de acciones y se suelta al confirmar o cancelar.
   */
  const [pinnedStaffId, setPinnedStaffId] = useState<number | undefined>(undefined);

  /**
   * La búsqueda la resuelve el servidor, así que cada tecla sería un pedido. Se
   * espera a que la escritura se detenga: media palabra no es una búsqueda, es una
   * persona escribiendo.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearchText(searchText.trim());
      setPageNumber(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    props.GetAllStaff({
      PageNumber: pageNumber,
      PageSize: pageSize,
      SearchText: appliedSearchText || undefined
    });
  }, [pageNumber, pageSize, appliedSearchText]);

  // events
  const handleOpenCreate = () => {
    setSelectedStaffId(undefined);
    setSelectedPersonId(undefined);
    openForm();
  };

  const handleEdit = (member: Staff) => {
    setSelectedStaffId(member.id);
    setSelectedPersonId(member.person?.id);
    openForm();
  };

  const handleDismissForm = () => {
    setSelectedStaffId(undefined);
    setSelectedPersonId(undefined);
    dismissForm();
  };

  /**
   * La ficha se abre por el id de la PERSONA y no por el del miembro: adentro no
   * hay nada de staff, son los datos de la persona y sus identificadores,
   * teléfonos, correos y domicilios, todos numerados por persona.
   */
  const handleViewDetails = (member: Staff) => {
    if (!member.person?.id) return;
    navigate('/staff/' + member.person.id);
  };

  const handleRefresh = () => {
    props.GetAllStaff({
      PageNumber: pageNumber,
      PageSize: pageSize,
      SearchText: appliedSearchText || undefined
    });
  };

  //#region Subcontent definition

  const Controls = (
    <div className="staff-list__header">
      <SearchBox
        className="staff-list__search"
        placeholder={t('staffList.searchPlaceholder', { defaultValue: 'Buscar miembro del staff' })}
        underlined={false}
        value={searchText}
        onChange={(
          event?: React.ChangeEvent<HTMLInputElement> | undefined,
          newValue?: string | undefined
        ) => setSearchText(newValue || '')}
        onClear={() => setSearchText('')}
      />
      <div className="staff-list__actions">
        <PrimaryButton
          text={t('staffList.button.addStaff', { defaultValue: 'Agregar miembro' })}
          iconProps={ComponentFunctions.AddIcon}
          onClick={handleOpenCreate}
        />
        <DefaultButton
          iconProps={ComponentFunctions.RefreshIcon}
          title={t('staffList.button.refresh', { defaultValue: 'Actualizar' })}
          ariaLabel={t('staffList.button.refresh', { defaultValue: 'Actualizar' })}
          onClick={handleRefresh}
          disabled={props.isLoading}
        />
      </div>
    </div>
  );

  const PrincipalErrorMessage = props.failOnLoading && (
    <MessageComponent
      message={t('staffList.errorMessage.error', { defaultValue: 'No fue posible obtener el staff' })}
      subMessage={props.error?.ErrorMessage || ''}
      type={MessageBarType.error}
      itemList={props.error?.Errors || []}
    />
  );

  const BrokenIdentifiersMessage = props.hasBrokenIdentifiers && (
    <>
      <MessageComponent
        message={t('staffList.identifiers.brokenMessage', {
          defaultValue: 'Se detectaron personas sin identificador único en esta página'
        })}
        subMessage={t('staffList.identifiers.brokenSubMessage', {
          defaultValue: 'Ejecute la corrección de identificadores para regularizarlas'
        })}
        type={MessageBarType.warning}
      />
      <DefaultButton
        text={
          props.isFixingIdentifiers
            ? t('staffList.identifiers.fixing', { defaultValue: 'Corrigiendo...' })
            : t('staffList.identifiers.fix', { defaultValue: 'Corregir identificadores' })
        }
        onClick={() => props.FixPersonIdentifiers()}
        disabled={props.isFixingIdentifiers}
        className="optionButton"
      />
    </>
  );

  const EmptyMessage = !props.isLoading && props.staff.length === 0 && (
    <div className="staff-list__empty">
      <FontIcon iconName="ContactCard" className="staff-list__empty-icon" />
      <p>
        {appliedSearchText
          ? t('staffList.emptySearch', {
              defaultValue: 'Ningún miembro del staff coincide con la búsqueda'
            })
          : t('staffList.empty', { defaultValue: 'No hay miembros del staff registrados' })}
      </p>
    </div>
  );

  const CardGrid = props.staff.length > 0 && (
    <div className="staff-list__grid">
      {props.staff.map((member: Staff) => {
        const isJuridical = member.person?.personType === PersonTypeEnum.JuridicalPerson;
        const isBroken = ComponentFunctions.HasBrokenIdentifier(member);
        const displayName = ComponentFunctions.GetStaffDisplayName(member);
        const hasPerson = !!member.person?.id;

        return (
          <div key={member.id} className="staff-card-wrapper">
            <div
              className={
                'staff-card' +
                (isBroken ? ' staff-card--broken' : '') +
                (pinnedStaffId === member.id ? ' staff-card--pinned' : '')
              }
            >
              <div className="staff-card__accent" />
              <div className="staff-card__body">
              <div className="staff-card__header">
                <button
                  type="button"
                  className="staff-card__name-button"
                  onClick={() => handleViewDetails(member)}
                  disabled={!hasPerson}
                  title={t('staffList.parameters.buttons.detailsButton', { defaultValue: 'Ver ficha' })}
                >
                  <FontIcon iconName="ContactCard" className="staff-card__user-icon" />
                  <span className="staff-card__name">{displayName}</span>
                </button>
                <span
                  className={
                    'staff-card__badge' + (isJuridical ? ' staff-card__badge--juridical' : '')
                  }
                >
                  {isJuridical
                    ? t('persons.personType.juridical', { defaultValue: 'Persona jurídica' })
                    : t('persons.personType.natural', { defaultValue: 'Persona física' })}
                </span>
              </div>

              <div className="staff-card__meta">
                <span className="staff-card__meta-label">
                  {t('staffList.parameters.id', { defaultValue: 'ID' })}:
                </span>
                <strong>{member.id}</strong>
                {isBroken && (
                  <span className="staff-card__warning">
                    {' · '}
                    {t('staffList.identifiers.missing', { defaultValue: 'Sin identificador único' })}
                  </span>
                )}
              </div>

              {/* Al tocar cualquier acción la tarjeta queda anclada: si alguna
                  abre una confirmación, encogerse acá dejaría el callout huérfano
                  y el botón fuera del alcance del puntero. */}
              <div
                className="staff-card__actions"
                onClick={() => setPinnedStaffId(member.id)}
              >
                <IconButton
                  iconProps={ComponentFunctions.ViewIcon}
                  title={t('staffList.parameters.buttons.detailsButton', { defaultValue: 'Ver ficha' })}
                  ariaLabel={t('staffList.parameters.buttons.detailsButton', { defaultValue: 'Ver ficha' })}
                  onClick={() => handleViewDetails(member)}
                  disabled={!hasPerson}
                />
                <IconButton
                  iconProps={ComponentFunctions.EditIcon}
                  title={t('staffList.parameters.buttons.modifyButton', { defaultValue: 'Modificar' })}
                  ariaLabel={t('staffList.parameters.buttons.modifyButton', { defaultValue: 'Modificar' })}
                  onClick={() => handleEdit(member)}
                  disabled={!hasPerson}
                />
                <div className="danger">
                  <ConfirmationButtonComponent
                    ButtonId={`StaffDeleteButton-${member.id}`}
                    ButtonIconName={'Delete'}
                    ButtonText={''}
                    ButtonCssClass={''}
                    ConfirmationButtonText={t('staffList.delete.confirm', { defaultValue: 'Eliminar' })}
                    ConfirmationButtonCssClass={'deleteButton'}
                    CancelButtonText={t('staffList.delete.cancel', { defaultValue: 'Cancelar' })}
                    CancelButtonCssClass={''}
                    Title={t('staffList.delete.title', { defaultValue: 'Eliminar miembro del staff' })}
                    Description={t('staffList.delete.description', {
                      defaultValue: '¿Desea eliminar al miembro del staff seleccionado?'
                    })}
                    ElementTextHeader={t('staffList.delete.element', { defaultValue: 'Miembro' })}
                    ElementTextInformation={displayName}
                    onConfirmation={() => {
                      props.DeleteStaff(member.id);
                      setPinnedStaffId(undefined);
                    }}
                    onCancel={() => setPinnedStaffId(undefined)}
                    isOperationExecuting={props.isDeleting || false}
                    isOperationExecutedWithError={props.failOnDeleting || false}
                    OperationError={props.error}
                  />
                </div>
              </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  /* Sólo en la primera carga. Al cambiar de página o al buscar, la grilla anterior
     se queda a la vista mientras llega la nueva y el spinner del propio control de
     paginación avisa que algo está en curso: reemplazarla por un spinner haría
     saltar la pantalla en cada tecla. */
  const Loading = props.isLoading && props.staff.length === 0 && (
    <div className="staff-list__loading">
      <Spinner
        label={t('staffList.loading', { defaultValue: 'Cargando staff...' })}
        ariaLive="assertive"
        labelPosition="right"
      />
    </div>
  );

  const Pagination = props.pagination.totalRecords > 0 && (
    <PaginationControlComponent
      paginationState={props.pagination}
      showedRecords={props.staff.length}
      isLoading={props.isLoading || false}
      setPageNumber={setPageNumber}
      setPageSize={setPageSize}
    />
  );

  //#endregion

  return (
    <>
      {Controls}
      {PrincipalErrorMessage}
      {BrokenIdentifiersMessage}
      {EmptyMessage}
      {CardGrid}
      {Loading}
      {Pagination}
      <PersonFormComponent
        isOpen={isFormOpen}
        openPanel={openForm}
        dismissPanel={handleDismissForm}
        staffId={selectedStaffId}
        personId={selectedPersonId}
      />
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(StaffListComponent as any);
